import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null); // { pending_id, name, email, picture, invite_error? }
  const [inviteCode, setInviteCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      try {
        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
          setError("session_id mancante");
          return;
        }
        const session_id = decodeURIComponent(match[1]);
        window.history.replaceState({}, document.title, window.location.pathname);

        const res = await api.authGoogleCallback(session_id);
        if (res?.needs_invite) {
          // Fase beta: nuovo account → richiesta codice invito
          setPending({
            pending_id: res.pending_id,
            name: res.name,
            email: res.email,
            picture: res.picture,
            invite_error: res.invite_error,
          });
          return;
        }
        try { localStorage.removeItem("mag_guest"); } catch (_e) { /* noop */ }
        if (res?.is_new_user) {
          try { localStorage.setItem("mag_welcome_seen", "0"); } catch (_e) { /* noop */ }
        }
        await refresh();
        toast.success(`Benvenuto${res?.user?.name ? `, ${res.user.name}` : ""}!`);
        navigate("/app/dashboard", { replace: true, state: { user: res?.user } });
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        setError(detail);
        toast.error("Accesso fallito", { description: detail });
      }
    })();
  }, [navigate, refresh]);

  const submitInvite = async (e) => {
    e?.preventDefault?.();
    if (!pending) return;
    const code = inviteCode.trim().toUpperCase();
    if (!code) {
      toast.error("Inserisci il codice invito");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.authGoogleCompleteInvite(pending.pending_id, code);
      try { localStorage.removeItem("mag_guest"); } catch (_e) { /* noop */ }
      if (res?.is_new_user) {
        try { localStorage.setItem("mag_welcome_seen", "0"); } catch (_e) { /* noop */ }
      }
      await refresh();
      toast.success(`Benvenuto${res?.user?.name ? `, ${res.user.name}` : ""}!`);
      navigate("/app/dashboard", { replace: true, state: { user: res?.user } });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error("Codice non valido", { description: detail });
      setPending((p) => (p ? { ...p, invite_error: detail } : p));
    } finally {
      setSubmitting(false);
    }
  };

  if (pending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-4">
        <div className="w-full max-w-md bg-white border border-[#E8E4DA] rounded-2xl p-6 sm:p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#2E4A3B] text-white flex items-center justify-center">
              <KeyRound size={20} />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-[#2E4A3B]" style={{ fontFamily: "Work Sans, sans-serif" }}>
                Codice invito richiesto
              </h1>
              <p className="text-xs text-[#5C5F5A]">Ciao {pending.name} — InPalmo è in beta chiusa.</p>
            </div>
          </div>
          <p className="text-sm text-[#5C5F5A] mb-4">
            Per completare l&apos;attivazione del tuo account inserisci il codice invito che hai ricevuto.
            <br />
            <span className="text-xs">L&apos;accesso ospite/demo rimane sempre libero, senza codice.</span>
          </p>
          <form onSubmit={submitInvite} className="space-y-3">
            <Input
              data-testid="invite-code-input"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="Inserisci il tuo codice"
              className="uppercase tracking-widest text-center font-mono"
              autoFocus
            />
            {pending.invite_error && (
              <p className="text-xs text-red-600" data-testid="invite-code-error">{pending.invite_error}</p>
            )}
            <Button
              type="submit"
              disabled={submitting || !inviteCode.trim()}
              className="w-full bg-[#2E4A3B] hover:bg-[#243c2f] text-white"
              data-testid="invite-code-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Attiva account
            </Button>
            <p className="text-[11px] text-[#5C5F5A] text-center pt-1 leading-relaxed">
              Attivando l&apos;account accetti i{" "}
              <a href="/termini" target="_blank" rel="noopener noreferrer" className="text-[#2E4A3B] underline hover:text-[#243c2f]">
                Termini
              </a>{" "}
              e la{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#2E4A3B] underline hover:text-[#243c2f]">
                Privacy Policy
              </a>
              .
            </p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="w-full text-xs text-[#5C5F5A] hover:text-[#2E4A3B] underline pt-1"
            >
              Torna alla home
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
      <div className="text-center space-y-3">
        <Loader2 size={32} className="animate-spin text-[#2E4A3B] mx-auto" />
        <div className="text-sm text-[#5C5F5A]" style={{ fontFamily: "Work Sans, sans-serif" }}>
          {error ? `Errore: ${error}` : "Accesso in corso..."}
        </div>
        {error && (
          <button onClick={() => navigate("/")} className="text-sm text-[#2E4A3B] underline">
            Torna alla home
          </button>
        )}
      </div>
    </div>
  );
}
